import React, { FC, useEffect, useRef } from 'react'
import { Link, useTranslation } from 'gatsby-plugin-react-i18next'
import { useLocation } from '@reach/router'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { createShowFromBottomAnimation } from 'src/helpers/animations'

import SectionHeader, { ArrowRightIcon } from 'src/components/ui/SectionHeader'

gsap.registerPlugin(ScrollTrigger)

const AnimatedText: FC<{ children: React.ReactNode; isActive?: boolean }> = ({ children, isActive }) => (
  <span className="relative block overflow-hidden">
    {/* First span: Normal flow, determines container size */}
    <span className={`block transition-transform duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] transform ${!isActive ? 'lg:group-hover:-translate-y-full' : '-translate-y-full'} will-change-transform [backface-visibility:hidden] [transform:translateZ(0)]`}>
      {children}
    </span>
    {/* Second span: Absolute, slides in from bottom */}
    <span className={`absolute inset-0 block transition-transform duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] transform ${!isActive ? 'translate-y-full lg:group-hover:translate-y-0' : 'translate-y-0'} will-change-transform [backface-visibility:hidden] [transform:translateZ(0)] text-red`}>
      {children}
    </span>
  </span>
)

const ContactSection: FC = () => {
  const { t } = useTranslation(['contact-section', 'side-menu', 'footer'])
  const location = useLocation()

  const subtitle = t('subtitle', { ns: 'contact-section' })
  const firstPartOfTitle = t('first-part-of-title', { ns: 'contact-section' })
  const secondPartOfTitle = t('second-part-of-title', { ns: 'contact-section' })
  const content = t('content', { ns: 'contact-section' })
  const isContactPage = location.pathname.includes('/contact')
  const ctaLink = isContactPage ? '#contact' : '/contact'
  const ctaText = t('button-text', { ns: 'contact-section' })

  const navigationLinks = [
    { name: t('home', { ns: 'side-menu' }), url: '/' },
    { name: t('sport', { ns: 'side-menu' }), url: '/sport', partiallyActive: true },
    { name: t('services', { ns: 'side-menu' }), url: '/services', partiallyActive: true },
    { name: t('case study', { ns: 'side-menu' }), url: '/case-studies', partiallyActive: true },
    { name: t('technology', { ns: 'side-menu' }), url: '/technology-radar' },
    { name: t('career', { ns: 'side-menu' }), url: '/career' },
    // { name: 'design system', url: '/design-system', partiallyActive: true },
    { name: t('contact', { ns: 'side-menu' }), url: '/contact' },
  ]

  const legalsLinks = [
    { name: t('iso 27001', { ns: 'side-menu' }), url: '/iso' },
    { name: t('accessibility statement', { ns: 'side-menu' }), url: '/accessibility-statement', hideOnMobile: true },
    { name: t('security', { ns: 'side-menu' }), url: '/security' },
    { name: t('privacy policy / GPDR', { ns: 'side-menu' }), url: '/privacy-policy' },
    // { name: t('investor relations', { ns: 'side-menu' }), url: '/investor-relations' },
  ]

  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/grupamamf' },
    { name: 'LinkedIn', url: 'https://www.linkedin.com/company/memyfriends/mycompany/' },
    { name: 'Behance', url: 'https://www.behance.net/teammamf' },
    { name: 'Dribbble', url: 'https://dribbble.com/mamfteam' },
  ]

  const checkIsActive = (url: string, partiallyActive?: boolean) => {
    const currentPath = location.pathname.replace(/\/$/, '') || '/'
    const targetPath = url.replace(/\/$/, '') || '/'

    if (partiallyActive) {
      if (targetPath === '/') return currentPath === '/'
      return currentPath.startsWith(targetPath)
    }

    return currentPath === targetPath
  }

  const handleCtaClick = (e: React.MouseEvent) => {
    if (isContactPage) {
      e.preventDefault()
      const contactSection = document.getElementById('contact')
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const sectionRef = useRef<HTMLElement>(null)
  const navigationSectionRef = useRef<HTMLDivElement>(null)
  const legalsSectionRef = useRef<HTMLDivElement>(null)
  const mobileSocialsSectionRef = useRef<HTMLDivElement>(null)
  const socialsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (navigationSectionRef.current) {
      const navElements = Array.from(navigationSectionRef.current.children)
      const navTl = gsap.timeline()
      navElements.forEach((el) => {
        createShowFromBottomAnimation(navTl, el, navigationSectionRef.current || undefined)
      })
    }
    if (legalsSectionRef.current) {
      const legalsElements = Array.from(legalsSectionRef.current.children)
      const legalsTl = gsap.timeline()
      legalsElements.forEach((el) => {
        createShowFromBottomAnimation(legalsTl, el, legalsSectionRef.current || undefined)
      })
    }
    if (mobileSocialsSectionRef.current) {
      const mobileSocialsElements = Array.from(mobileSocialsSectionRef.current.children)
      const mobileSocialsTl = gsap.timeline()
      mobileSocialsElements.forEach((el) => {
        createShowFromBottomAnimation(mobileSocialsTl, el, mobileSocialsSectionRef.current || undefined, undefined, 'top 98%')
      })
    }
    if (socialsRef.current) {
      const socialElements = Array.from(socialsRef.current.children)
      const socialsTl = gsap.timeline()
      socialElements.forEach((el) => {
        createShowFromBottomAnimation(socialsTl, el, socialsRef.current || undefined, undefined, 'top 98%')
      })
    }

    ScrollTrigger.sort()

    // Refresh ScrollTrigger to ensure correct calculation of the bottom section
    const timer = setTimeout(() => {
      ScrollTrigger.sort()
      ScrollTrigger.refresh()
    }, 300)

    return () => clearTimeout(timer)
  }, [])

  return (
    <section
      ref={sectionRef}
      id="contact-footer"
      className="w-full relative z-10 min-h-screen flex flex-col overflow-hidden bg-transparent"
    >
      <div className="container mx-auto flex-grow flex flex-col pb-12">
        {/* Top Spacer to balance the bottom social links for perfect vertical centering */}
        <div className="hidden lg:block h-[80px] xl:h-[120px] shrink-0" aria-hidden="true" />

        {/* Mobile top padding */}
        <div className="block lg:hidden pt-20 md:pt-32 shrink-0" aria-hidden="true" />

        <div className="flex-grow w-full flex flex-col lg:flex-row justify-center lg:justify-between lg:items-center gap-12 lg:gap-0">
          {/* Left Column: Content */}
          <div className="w-full lg:w-7/12">
            <SectionHeader
              subtitle={subtitle}
              titlePart1={firstPartOfTitle}
              titlePart2={secondPartOfTitle}
              description={content}
              ctaText={ctaText}
              ctaLink={ctaLink}
              onCtaClick={handleCtaClick}
              isFullWidthDescription={true}
              stackActions={true}
            />
          </div>

          {/* Right Column: Navigation Columns */}
          <div className="w-full lg:w-5/12 grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 xl:gap-24 text-left lg:pl-12">
            {/* Navigation Column */}
            <div ref={navigationSectionRef} className="flex flex-col items-start">
              <p className="text-megaDarkGray dark:text-white/60 text-xs font-bold uppercase tracking-widest mb-6 opacity-60">Navigation</p>
              {navigationLinks.map((link) => {
                const isActive = checkIsActive(link.url, (link as any).partiallyActive)
                return (
                  <Link
                    key={link.name}
                    to={link.url}
                    className={`group flex items-center justify-start mb-4 text-xl lg:text-lg xl:text-xl font-palanquin font-bold leading-normal text-black dark:text-white relative lg:hover:opacity-100 ${isActive ? 'active font-bold' : 'opacity-60 dark:opacity-80 lg:hover:opacity-100'}`}
                  >
                    <AnimatedText isActive={isActive}>{link.name}</AnimatedText>
                  </Link>
                )
              })}
            </div>

            {/* Legals Column */}
            <div ref={legalsSectionRef} className="flex flex-col items-start">
              <p className="text-megaDarkGray dark:text-white/60 text-xs font-bold uppercase tracking-widest mb-6 opacity-60">Legals</p>
              {legalsLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.url}
                  className={`group ${(link as any).hideOnMobile ? 'hidden lg:flex' : 'flex'} items-center justify-start mb-4 text-xl lg:text-lg xl:text-xl font-palanquin font-bold leading-normal text-black dark:text-white relative lg:hover:opacity-100 ${checkIsActive(link.url) ? 'active font-bold' : 'opacity-60 dark:opacity-80 lg:hover:opacity-100'}`}
                >
                  <AnimatedText isActive={checkIsActive(link.url)}>{link.name}</AnimatedText>
                </Link>
              ))}
            </div>

            {/* Socials Column (Mobile only) */}
            <div ref={mobileSocialsSectionRef} className="lg:hidden flex flex-col items-start">
              <p className="text-megaDarkGray dark:text-white/60 text-xs font-bold uppercase tracking-widest mb-6 opacity-60">Socials</p>
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative inline-flex items-center mb-4 text-xl font-palanquin font-semibold text-on-surface select-none opacity-80 lg:hover:opacity-100"
                >
                  <span
                    className="absolute left-0 inline-flex items-center text-primary opacity-0 -translate-x-4 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] shrink-0 pointer-events-none"
                    aria-hidden="true"
                  >
                    <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" />
                  </span>
                  <span className="inline-block border-b border-dashed border-primary pb-0.5 transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:translate-x-8 md:group-hover:translate-x-9">
                    {link.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Socials Row (Desktop only) */}
        <div ref={socialsRef} className="hidden lg:flex w-full pt-8 xl:pt-12 border-t border-secondaryGray300 dark:border-white/10 justify-between gap-8">
          {socialLinks.map((link) => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="group relative inline-flex items-center text-lg xl:text-xl font-palanquin font-semibold text-on-surface select-none opacity-80 lg:hover:opacity-100"
            >
              <span
                className="absolute left-0 inline-flex items-center text-primary opacity-0 -translate-x-4 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] shrink-0 pointer-events-none"
                aria-hidden="true"
              >
                <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" />
              </span>
              <span className="inline-block border-b border-dashed border-primary pb-0.5 transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:translate-x-8 md:group-hover:translate-x-9">
                {link.name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ContactSection
